import { useState } from 'react';
import { useIsMobile } from '../utils/useIsMobile';
import { formatCurrency, statusBadge, formatCurrencyWhole } from '../utils/api';
import { formatDateShort } from '../utils/dateFormat';
import { expectedUnitPrice, columnKeys } from '../utils/creditKind';
import { CREDIT_STATUS } from '../utils/creditsShared';
import CreditClosedBy from './CreditClosedBy';
import ListRow from './ListRow';
import Icon from './Icon';
import { HqOnlyLock } from './HqOnlyNotice';

// Two-line column header: a main label with a smaller qualifier beneath it
// (e.g. "סה״כ זיכוי" / "ללא מע״מ"). Keeps wide qualifiers off the main line so
// the column doesn't have to be as wide as the full one-line text.
function ColHead({ main, sub }) {
  return (
    <th>
      {main}
      <span style={{ display: 'block', fontWeight: 400, fontSize: 'var(--fs-xs)', color: 'var(--ink-subtle)', marginTop: 1 }}>{sub}</span>
    </th>
  );
}

// Module-level so the default prop is a stable reference across renders
// rather than a fresh Set each time.
const EMPTY_SELECTION = new Set();

// The per-kind grouped desktop tables + mobile list, shared by the open and
// history credits pages — everything about a credit ROW is identical between
// the two views; only which statuses are in the `groups` and which actions a
// row's ⋮ menu offers differ, both supplied by the caller.
//
// `selectMode`/`selectedIds`/`setSelectedIds` are only meaningful on the open
// page (bulk mark-as-received) — the history page passes `selectMode={false}`
// and no selection UI renders at all.
//
// `renderRowActions(credit)` returns the status-specific menu items (a
// fragment of <button>s); this component always prepends "צפה במסמך" — and, on
// a CLOSED credit, "הערות" (the close audit) — plus a divider, since those are
// identical on every row regardless of status.
export default function CreditsTable({
  groups,
  canEdit,
  selectMode = false,
  // Defaulted, not merely documented as open-page-only: the history page
  // renders this table with selectMode={false} and passes neither. The
  // `allPicked` computation below reads selectedIds before any selectMode
  // guard, so without this default it would rely on a history group's
  // openIds always being empty to short-circuit — safe by accident. The
  // default makes it safe by construction.
  selectedIds = EMPTY_SELECTION,
  setSelectedIds,
  renderRowActions,
  vatMult,
  sortableTh,
  sortInd,
  onOpenDocument,
  onRowClick,
}) {
  // Table vs cards switches with the filter bar and the view tabs, at the
  // filter-system breakpoint (720) — not at 640, which is the DENSITY step.
  const isMobile = useIsMobile();
  // Open-row-menu state: holds the id of the row whose ⋮ menu is open, or
  // null. Single state so opening a menu auto-closes any other. Owned here
  // (not by the page) — nothing outside the table needs to know which row's
  // menu is open.
  const [openMenu, setOpenMenu] = useState(null);
  // The row whose close-audit ("הערות") popover is open. Separate state from
  // openMenu because the two are alternative contents of the SAME anchored
  // panel — picking הערות swaps the menu out for the audit rows rather than
  // stacking a second floating layer over it.
  const [notesFor, setNotesFor] = useState(null);
  const withVat = (n) => (n || 0) * vatMult;

  const toggleSelected = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // One desktop table row, rendered against its group's column set (`cols`).
  // "מחיר מצופה" (expected unit price) is shown only for rows that carry a
  // price diff.
  const renderRow = (c, cols) => {
    const menuBtn = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '0.88rem', textAlign: 'right', color: 'var(--ink)', fontFamily: 'inherit', direction: 'rtl' };
    const hasDiff = (c.credit_price_diff || 0) > 0;
    const isOpen  = c.status === CREDIT_STATUS.OPEN;
    const picked  = selectMode && selectedIds.has(c.id);
    // Only a CLOSED credit has a close record. An open one was never closed,
    // so there is nothing to show and the menu item must not appear at all.
    const hasNotes = Boolean(c.closed_by_label);
    // The floating layer's shared chrome — the menu and the audit popover are
    // the same panel with different contents, so they must not drift apart.
    const overlay = { position: 'fixed', inset: 0, zIndex: 198 };
    const panel   = { position: 'absolute', left: 0, top: '100%', zIndex: 199, background: '#fff', border: '1px solid var(--line)', borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,0.15)', overflow: 'hidden', padding: 4 };
    return (
      <tr key={c.id}
        onClick={selectMode && isOpen ? () => toggleSelected(c.id) : undefined}
        style={{
          background: picked ? 'var(--selected-soft-bg)' : isOpen ? 'var(--warning-bg)' : undefined,
          cursor: selectMode && isOpen ? 'pointer' : undefined,
          // Non-open rows can't be selected, so in selection mode they read as
          // out of scope rather than merely unchecked.
          opacity: selectMode && !isOpen ? 0.5 : undefined,
        }}>
        {selectMode && (
          <td style={{ width: 40, textAlign: 'center' }}>
            <input
              type="checkbox"
              checked={picked}
              disabled={!isOpen}
              onChange={() => toggleSelected(c.id)}
              onClick={e => e.stopPropagation()}
              aria-label={`בחר זיכוי — ${c.supplier_name}, ${c.item_description || ''}`}
            />
          </td>
        )}
        <td style={{ fontWeight: 600 }}>{c.supplier_name}<HqOnlyLock hqOnly={c.hq_only} /></td>
        {/* The doc number IS the link — a value styled as one (link colour,
            underline on hover) reads as clickable on its own, so it replaced
            the eye button that used to sit beside it. stopPropagation because
            in selection mode the whole <tr> is a toggle, and opening a
            document must not also (de)select its row. */}
        <td>
          <button
            type="button"
            className="cell-link"
            onClick={(e) => { e.stopPropagation(); onOpenDocument(c.document_id); }}
            title="צפה במסמך"
            aria-label={`צפה במסמך ${c.doc_number || ''}`.trim()}
          >{c.doc_number || '—'}</button>
        </td>
        <td>{formatDateShort(c.doc_date) || '—'}</td>
        <td>{c.item_description}</td>
        <td style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>{c.item_sku || '—'}</td>
        {cols.qty  && <td>{c.credit_qty || '—'}</td>}
        {cols.diff && <td>{hasDiff ? formatCurrency(c.credit_price_diff) : '—'}</td>}
        <td>{formatCurrency(c.unit_price)}</td>
        {cols.expected && (
          <td style={{ color: hasDiff ? 'var(--success-ink)' : 'var(--ink-subtle)', fontWeight: hasDiff ? 600 : 400 }}>
            {hasDiff ? formatCurrency(expectedUnitPrice(c)) : '—'}
          </td>
        )}
        <td style={{ fontWeight: 700, color: 'var(--warning-ink)' }}>{formatCurrencyWhole(c.credit_total)}</td>
        <td style={{ fontWeight: 700, color: 'var(--warning-ink)' }}>{formatCurrencyWhole(withVat(c.credit_total))}</td>
        <td><span className={`badge ${statusBadge(c.status)}`}>{c.status}</span></td>
        <td style={{ position: 'relative', width: 36, textAlign: 'center' }}>
          {/* The menu is not admin-only any more: "הערות" is a read of the
              audit trail, and it replaced a COLUMN that every role could see.
              Gating the whole ⋮ on canEdit would have quietly taken that
              reading away from viewers. The write actions inside stay gated. */}
          {(canEdit || hasNotes) && (<>
          <button
            type="button"
            // stopPropagation for the same reason as the eye button above:
            // opening a row's action menu must not also toggle that row into
            // the bulk selection, or an abandoned per-row action leaves a
            // credit silently checked and it gets swept into the next
            // "סמן כהתקבל".
            onClick={(e) => { e.stopPropagation(); setNotesFor(null); setOpenMenu(openMenu === c.id ? null : c.id); }}
            aria-label="פעולות"
            aria-expanded={openMenu === c.id || notesFor === c.id}
            className="row-menu-trigger" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--ink-muted)', padding: '2px 6px', borderRadius: 6, lineHeight: 1 }}
          >⋮</button>
          {openMenu === c.id && (
            <>
              <div style={overlay} onClick={() => setOpenMenu(null)} />
              <div style={{ ...panel, minWidth: 180 }}>
                <button className="row-menu-item" style={menuBtn}
                  onClick={() => { setOpenMenu(null); onOpenDocument(c.document_id); }}>
                  <Icon name="eye" size={16} strokeWidth={1.5} /> צפה במסמך
                </button>
                {/* Who closed this credit, when and why — the audit that used
                    to be a permanent column. Offered only on a closed credit:
                    an open one has no close record, and a menu item that opens
                    an empty panel is worse than no menu item. */}
                {hasNotes && (
                  <button className="row-menu-item" style={menuBtn}
                    onClick={() => { setOpenMenu(null); setNotesFor(c.id); }}>
                    <Icon name="info" size={16} strokeWidth={1.5} wash={false} /> הערות
                  </button>
                )}
                {canEdit && (<>
                  <div style={{ borderTop: '1px solid var(--hairline)', margin: '4px 0' }} />
                  {renderRowActions(c, () => setOpenMenu(null), menuBtn)}
                </>)}
              </div>
            </>
          )}
          {notesFor === c.id && (
            <>
              <div style={overlay} onClick={() => setNotesFor(null)} />
              {/* Same anchored panel as the menu, wider: it holds label/value
                  rows rather than a list of one-line actions, and the address
                  shows in FULL here — the column it replaced clipped it behind
                  a `title` tooltip. stopPropagation so a click inside the
                  panel neither closes it via the overlay nor toggles the row
                  into a bulk selection. */}
              <div
                role="dialog"
                aria-label="הערות סגירת הזיכוי"
                onClick={(e) => e.stopPropagation()}
                style={{ ...panel, width: 300, direction: 'rtl', padding: '2px 14px 8px', cursor: 'default', fontWeight: 400 }}
              >
                <CreditClosedBy credit={c} />
              </div>
            </>
          )}
          </>)}
        </td>
      </tr>
    );
  };

  return (
    <>
      {/* One table per credit kind rather than one table with a shared
          header: each kind populates a different set of numeric columns
          (see KIND_COLS), and a shared header forces every group to carry
          the union — three columns of '—' on the commonest group, and a
          table too wide for a 13" laptop. */}
      {!isMobile && (
      <div>
        {groups.map(g => {
          // Single source of truth for the column set — the same helper the
          // header below is built from, so the subtotal's colSpan can't drift
          // from the columns it has to span (guarded by creditKind.test.js).
          const keys = columnKeys(g.cols, selectMode);
          // Everything before the two money columns collapses into the
          // subtotal row's label cell.
          const leadSpan = keys.indexOf('net');
          // ...and everything AFTER them collapses into one filler cell. Stays
          // DERIVED rather than the literal it once was: the audit column came
          // and went again, and each time the literal left the footer a cell
          // short of the header. Guarded by creditKind.test.js.
          const tailSpan = keys.length - (leadSpan + 2);
          const allPicked = g.openIds.length > 0 && g.openIds.every(id => selectedIds.has(id));
          return (
            <div key={g.key} style={{ marginBottom: 20 }}>
              <div style={{ background: 'var(--tint-strong)', fontWeight: 700, color: 'var(--ink)',
                            padding: '8px 16px', borderTop: '2px solid var(--outline)',
                            borderRadius: '6px 6px 0 0' }}>
                {g.label} <span style={{ color: 'var(--ink-muted)', fontWeight: 500 }}>({g.rows.length})</span>
              </div>
              <div className="table-wrapper">
                <table className="credits-table">
                  <thead>
                    <tr>
                      {selectMode && (
                        <th style={{ width: 40, textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={allPicked}
                            disabled={g.openIds.length === 0}
                            onChange={() => setSelectedIds(prev => {
                              const next = new Set(prev);
                              if (allPicked) g.openIds.forEach(id => next.delete(id));
                              else           g.openIds.forEach(id => next.add(id));
                              return next;
                            })}
                            title="בחר את כל הזיכויים הפתוחים בקבוצה"
                            aria-label={`בחר את כל הזיכויים הפתוחים — ${g.label}`}
                          />
                        </th>
                      )}
                      <th {...sortableTh('supplier')}>שם ספק{sortInd('supplier')}</th>
                      <ColHead main="מספר" sub="מסמך" />
                      <th {...sortableTh('date')}>תאריך{sortInd('date')}</th>
                      <th>תיאור פריט</th>
                      <th>מק"ט</th>
                      {g.cols.qty      && <ColHead main="כמות" sub="לזיכוי" />}
                      {g.cols.diff     && <ColHead main="הפרש" sub="מחיר" />}
                      <ColHead main="מחיר" sub="בחשבונית" />
                      {g.cols.expected && <ColHead main="מחיר" sub="מצופה" />}
                      <th {...sortableTh('total')}>
                        סה״כ זיכוי{sortInd('total')}
                        <span style={{ display: 'block', fontWeight: 400, fontSize: 'var(--fs-xs)', color: 'var(--ink-subtle)', marginTop: 1 }}>ללא מע״מ</span>
                      </th>
                      <ColHead main="סה״כ זיכוי" sub="כולל מע״מ" />
                      <th>סטטוס</th>
                      <th>פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map(c => renderRow(c, g.cols))}
                    <tr style={{ background: 'var(--tint)', fontWeight: 700 }}>
                      <td colSpan={leadSpan} style={{ padding: '8px 16px', color: 'var(--ink)' }}>
                        סיכום — {g.count} זיכויים{g.count !== g.rows.length ? ' (ללא מבוטלים)' : ''}
                      </td>
                      <td style={{ color: 'var(--warning-ink)' }}>{formatCurrencyWhole(g.net)}</td>
                      <td style={{ color: 'var(--warning-ink)' }}>{formatCurrencyWhole(g.gross)}</td>
                      <td colSpan={tailSpan} />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Mobile: two-line list rows of the same credit list, grouped like the
          desktop tables. Row shows supplier / amount / status; tap opens a
          detail sheet with the rest + status actions. */}
      {isMobile && (
      <div>
        {groups.map(g => (
          <div key={g.key} style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                          padding: '6px 4px', marginBottom: 8, borderBottom: '2px solid var(--outline)' }}>
              <span style={{ fontWeight: 700, color: 'var(--ink)' }}>
                {g.label} <span style={{ color: 'var(--ink-muted)', fontWeight: 500 }}>({g.rows.length})</span>
              </span>
              {/* The VAT basis is stated here because it governs both this
                  subtotal AND the amount column of every row beneath it —
                  the mobile rows have no header to carry the qualifier. */}
              <span style={{ fontWeight: 700, color: 'var(--warning-ink)', fontSize: '0.9rem' }}>
                {formatCurrencyWhole(g.net)}
                <span style={{ color: 'var(--ink-subtle)', fontWeight: 500, fontSize: '0.78rem' }}> ללא מע״מ</span>
              </span>
            </div>
            <div className="m-list">
              {g.rows.map(c => {
                const isOpen = c.status === CREDIT_STATUS.OPEN;
                const picked = selectMode && selectedIds.has(c.id);
                return (
                  <ListRow
                    key={c.id}
                    // In selection mode the tint must show what is PICKED, so
                    // the open-row warn tint stands down for it.
                    className={picked ? 'is-selected' : (isOpen && !selectMode ? 'is-warn' : '')}
                    primary={<>{c.supplier_name}<HqOnlyLock hqOnly={c.hq_only} /></>}
                    meta={`${c.item_description || ''}${formatDateShort(c.doc_date) ? ` · ${formatDateShort(c.doc_date)}` : ''}`}
                    badge={<span className={`badge ${statusBadge(c.status)}`}>{c.status}</span>}
                    amount={<span style={{ color: 'var(--warning-ink)' }}>{formatCurrencyWhole(c.credit_total)}</span>}
                    variant={selectMode ? 'plain' : 'nav'}
                    onClick={selectMode
                      ? (isOpen ? () => toggleSelected(c.id) : undefined)
                      : () => onRowClick(c)}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
      )}
    </>
  );
}
